import { Fragment, useCallback, useEffect } from 'react';
import { debounce } from 'throttle-debounce';
import { useTranslation } from 'react-i18next';
// Types
import CaseStatuses from 'app/types/CaseStatuses';
// Store
import { useAppDispatch, useAppSelector } from 'app/hooks/useStore';
// Async
import { getInsuranceCases } from 'app/store/Cases/Cases.async';
// Selectors
import { selectCaseOptions } from 'app/store/Cases/Cases.selectors';
// Mui
import {
  CircularProgress, Autocomplete, TextField,
  FormHelperText
} from '@mui/material';
import { DATA_LIMIT } from 'app/App.constants';

type Props = {
  submitted: boolean;
  selectedCase: any;
  onSetSelectedCase: (caseId: number | null) => void;
}

export default function CaseStepContent({ submitted, selectedCase, onSetSelectedCase }: Props) {
  const { t } = useTranslation();
  // Dispatch
  const dispatch = useAppDispatch();
  // State
  const caseOptions = useAppSelector(selectCaseOptions);

  // eslint-disable-next-line
  const debounceGetCases = useCallback(debounce(500, (search:string) => {
    dispatch(getInsuranceCases({
      offset: 0,
      limit: DATA_LIMIT,
      search,
      status: `${CaseStatuses.Open},${CaseStatuses.Archived}`
    }))
  }), []);

  useEffect(() => {
    debounceGetCases('');
    // eslint-disable-next-line
  }, []);

  const handleInputChange = (_:any, value:string) => {
    debounceGetCases(value);
  }

  const handleChange = (_:any, newValue:any) => {
    onSetSelectedCase(newValue);
  }

  const initialCaseOptions = caseOptions || [];
  const caseLoading = !initialCaseOptions;

  return (
    <Autocomplete
      value={selectedCase}
      onChange={handleChange}
      onInputChange={handleInputChange}
      options={caseOptions || []}
      loading={caseLoading}
      fullWidth
      renderInput={(params:any) => (
        <Fragment>
          <TextField
            {...params}
            label={t('form.placeholder.findCase')}
            slotProps={{
              input: {
                ...params.InputProps,
                endAdornment: (
                  <Fragment>
                    {caseLoading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </Fragment>
                )
              }
            }}
            error={submitted && !selectedCase}
            helperText={submitted && !selectedCase ? t('validations.fieldRequired') : ''}
            margin="normal"
            fullWidth
          />
        </Fragment>
      )}
      renderOption={(props:any, option:any) => (
        <li {...props} key={option.id}>
          {option.name}
        </li>
      )}
      getOptionLabel={(option:any) => option.name}
      isOptionEqualToValue={(option:any, value:any) => option.id === value.id}
    />
  )
}
